import { useDevice } from '../../../utils/hooks/useDevice';

/**
 * The desktop redesign - Figma page "BOGI DRAFT", section "Desktop" (1635:4965). It draws the
 * home screen three times: "mackbook air" at 1280x832 (1625:4506), 1440x1024 (1648:252) and
 * 1920x1080 (1667:371). The three are one layout at one scale: every element of the 1440 and
 * 1920 frames is the 1280 frame's multiplied by min(width / 1280, height / 832) - 1.125 and 1.3.
 * So every number in the desktop layer is in the 1280x832 design space and is multiplied by
 * that one factor.
 *
 * Only the side margins, the top bar, the dock and the content block keep that fixed scale. The
 * leftover width goes to the content: at 1920 the left column grows to 1046 while the cards grid
 * stays at 576 x 1.3, and the leftover height is split above and below the content block (the
 * 1440 frame centres it in a taller column).
 */
export const DESKTOP_FRAME = { width: 1280, height: 832 } as const;

/** 32 either side of the top bar, the content and the dock. */
export const DESKTOP_SIDE = 32;
/** Figma "Top Bar": 88 high, 12 from the top. */
export const DESKTOP_TOP_BAR = { top: 12, height: 88 } as const;
/** Figma "Content" starts 12 under the top bar. */
export const DESKTOP_CONTENT_TOP = 112;
/**
 * The content block ends 704 down the 832 frame - 128 above the bottom edge. The dock's bar
 * starts at 752 and only the Home notch (from 712) and the raised Home button (from 696) rise
 * past that, between the two columns.
 */
export const DESKTOP_CONTENT_BOTTOM_INSET = 128;
/** Figma "Bottom Dock": 136 high including the raised Home button, flush with the bottom. */
export const DESKTOP_DOCK_HEIGHT = 136;

/** The one factor every desktop number is multiplied by. */
export const getDesktopScale = (width: number, height: number) =>
	Math.min(width / DESKTOP_FRAME.width, height / DESKTOP_FRAME.height);

export type DesktopLayout = {
	/** Web, at least 768 wide and landscape - the only viewports the desktop layer draws on. */
	isDesktopLayout: boolean;
	/** Design-to-pixel factor. */
	scale: number;
	/** Scales a design value. */
	s: (value: number) => number;
	width: number;
	height: number;
	/** Pixel inset of the content from either side. */
	side: number;
	/** Pixel box the screens lay their content out in, between the top bar and the dock. */
	contentTop: number;
	contentBottomInset: number;
	contentWidth: number;
	contentHeight: number;
};

export const useDesktopLayout = (): DesktopLayout => {
	const { isDesktop, width, height } = useDevice();
	const scale = getDesktopScale(width, height);
	const s = (value: number) => value * scale;
	const side = s(DESKTOP_SIDE);
	const contentTop = s(DESKTOP_CONTENT_TOP);
	const contentBottomInset = s(DESKTOP_CONTENT_BOTTOM_INSET);

	return {
		isDesktopLayout: isDesktop,
		scale,
		s,
		width,
		height,
		side,
		contentTop,
		contentBottomInset,
		contentWidth: Math.max(0, width - side * 2),
		contentHeight: Math.max(0, height - contentTop - contentBottomInset),
	};
};

/**
 * Softens the bottom edge of a scrolling desktop content box, which otherwise cuts its content
 * off in a hard line just above the dock. react-native-web passes the mask straight to CSS.
 */
export const desktopScrollFade = (fade: number) =>
	({
		maskImage: `linear-gradient(to bottom, #000 calc(100% - ${fade}px), transparent)`,
		WebkitMaskImage: `linear-gradient(to bottom, #000 calc(100% - ${fade}px), transparent)`,
	}) as any;
